import { Star, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionHeading } from "@/components/Reveal";

const REVIEWS = [
  {
    name: "Daniel M.",
    meta: "Ranger Raptor — Dual Channel Pro",
    text: "Can't see a single wire anywhere. He even walked me through the app before leaving. Flawless job.",
  },
  {
    name: "Priya S.",
    meta: "Zeekr 7X — Supply + Install",
    text: "Was nervous about trim damage on the Zeekr. Autoric knew exactly which clips and fuse points to use. Zero marks.",
  },
  {
    name: "Glen W.",
    meta: "HiLux Work Fleet — 6 vehicles",
    text: "Six utes done over two weekends, all identical installs with paperwork for our insurer. Proper professional.",
  },
];

const FAQS = [
  {
    q: "Will hardwiring drain my battery?",
    a: "No. Every hardwire kit we fit includes a voltage cut-off that shuts the camera down before your battery drops below starting level. You get 24/7 parking mode with a guaranteed start every morning.",
  },
  {
    q: "Will installation void my new-car warranty?",
    a: "No. We use non-invasive fuse taps and existing factory trim clips — nothing is cut, soldered or drilled into your vehicle's wiring. The install is fully reversible, which keeps your manufacturer warranty intact.",
  },
  {
    q: "Do you come to me?",
    a: "Yes — we're a fully mobile service covering the whole Perth metro area. We install at your home, workplace or anywhere with safe off-street parking. Just tell us your suburb when you enquire.",
  },
  {
    q: "How long does an install take?",
    a: "A front-only fitment takes around 30–45 minutes. A dual-channel front and rear hardwire takes 60–90 minutes depending on the vehicle.",
  },
  {
    q: "Which camera brands do you install?",
    a: "All major brands — BlackVue, VIOFO, Thinkware, Garmin, Nextbase and more. Bring your own unit, or choose a supply + install bundle and we'll bring a 4K dual-channel camera with us.",
  },
];

const TrustFaq = () => (
  <section id="faq" data-testid="trust-faq-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
    <SectionHeading eyebrow="Trust signals" title="Drivers talk. We deliver." testId="trust-heading" />

    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
      {REVIEWS.map((r, i) => (
        <Reveal key={r.name} delay={i * 0.12}>
          <figure data-testid={`review-card-${i + 1}`} className="h-full rounded-xl border border-white/10 bg-carbon p-7 hover:border-sky-500/30 transition-colors duration-500">
            <Quote size={22} className="text-sky-500/70" />
            <blockquote className="mt-4 text-sm text-gray-300 leading-relaxed">{r.text}</blockquote>
            <figcaption className="mt-6">
              <div className="flex gap-1 mb-2">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={13} className="fill-sky-500 text-sky-500" />
                ))}
              </div>
              <p className="font-display font-bold uppercase tracking-wide text-gray-50">{r.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">{r.meta}</p>
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
      <Reveal className="lg:col-span-2">
        <h3 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl text-gray-50 leading-tight">
          Straight answers, <span className="text-sky-500">before</span> you book
        </h3>
        <p className="mt-4 text-sm text-gray-400 leading-relaxed max-w-sm">
          Battery safety, warranty worries, hidden wires — the questions every driver asks, answered up front.
        </p>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-3">
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i + 1}`} className="font-display font-bold uppercase tracking-wide text-lg text-gray-100 hover:text-sky-400 hover:no-underline text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${i + 1}`} className="text-sm text-gray-400 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export default TrustFaq;
